import React, { useState } from "react";
import { Calendar } from "../components/ui/calendar";
import { toast } from "sonner";
import axios from "axios";
import { API } from "../App";
import { CalendarIcon, Phone, User, FileText, Clock, CheckCircle, Sparkles } from "lucide-react";

const services = [
  "Stucco", "Siding", "Soffit/Facia", "Roof Repair",
  "Door Installation", "Window Installation", "Screen Installation",
  "Exterior Paint", "Interior Paint", "Drywall Installation",
  "Flooring", "Tile", "Cabinet Installation", "Baseboards",
  "Minor Electrical", "Other"
];

const timeSlots = [
  "9:00 AM", "10:00 AM", "11:00 AM", "12:00 PM",
  "1:00 PM", "2:00 PM", "3:00 PM", "4:00 PM"
];

const BookingPage = () => {
  React.useEffect(() => { document.title = "Book an Appointment | New Leaf PMC LLC"; }, []);
  const [selectedDate, setSelectedDate] = useState(null);
  const [formData, setFormData] = useState({
    name: "", phone: "", email: "", address: "",
    service: "", preferred_time: "", notes: ""
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    // Only Name, Phone, Service are required
    if (!formData.name || !formData.phone || !formData.service) {
      toast.error("Please fill in name, phone, and service");
      return;
    }
    setIsSubmitting(true);
    try {
      await axios.post(`${API}/appointments`, {
        ...formData,
        preferred_date: selectedDate ? selectedDate.toISOString().split('T')[0] : ""
      });
      toast.success("Booked! We'll contact you shortly to confirm.");
      setSubmitted(true);
      if (window._track) window._track("booking_submit", { service: formData.service });
    } catch (error) {
      toast.error("Something went wrong. Call us at 863-340-9244");
    } finally {
      setIsSubmitting(false);
    }
  };

  if (submitted) {
    return (
      <div className="pt-16 md:pt-20 min-h-screen bg-[#0B0C10] flex items-center justify-center" data-testid="booking-success">
        <div className="max-w-md mx-auto px-4 text-center">
          <div className="w-20 h-20 rounded-full bg-green-500/20 flex items-center justify-center mx-auto mb-6">
            <CheckCircle size={40} className="text-green-400" />
          </div>
          <h1 className="text-3xl font-bold text-white mb-3">You're All Set!</h1>
          <p className="text-[#A0AAB2] mb-6">We'll call you within minutes to confirm your appointment. Check your phone!</p>
          <div className="space-y-3">
            <a href="tel:863-340-9244" className="block w-full gradient-btn py-4 rounded-xl font-bold text-lg text-center">
              <Phone size={20} className="inline mr-2" />Call Us: 863-340-9244
            </a>
            <button onClick={() => setSubmitted(false)} className="block w-full bg-[#15161C] border border-[#2A2B32] text-white py-3 rounded-xl font-medium">
              Book Another Appointment
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="pt-16 md:pt-20 min-h-screen bg-[#0B0C10]" data-testid="booking-page">
      {/* Header */}
      <section className="py-10 md:py-14 bg-[#15161C] border-b border-[#2A2B32]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="inline-flex items-center space-x-2 bg-green-500/20 text-green-400 px-3 py-1 rounded-full text-xs font-medium mb-4">
            <Sparkles size={12} />
            <span>Free estimates — no obligation</span>
          </div>
          <h1 className="text-3xl md:text-4xl font-bold text-white mb-3">Book Your Free Estimate</h1>
          <p className="text-[#A0AAB2] text-base max-w-xl mx-auto flex items-center justify-center space-x-2">
            <Clock size={16} className="text-[#FF6B35]" />
            <span>Get a response within minutes</span>
          </p>
        </div>
      </section>

      {/* Booking Form */}
      <section className="py-10 md:py-14">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <form onSubmit={handleSubmit}>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
              {/* Left: Essential Fields */}
              <div className="bg-[#15161C] border border-[#2A2B32] rounded-xl p-6">
                <h2 className="text-lg font-semibold text-white mb-5">Your Details</h2>
                <div className="space-y-4">
                  <div>
                    <label className="flex items-center space-x-2 text-sm font-medium text-white mb-2">
                      <User size={15} className="text-[#FF6B35]" />
                      <span>Name *</span>
                    </label>
                    <input type="text" value={formData.name} onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                      className="w-full px-4 py-3 bg-[#1F2027] border border-[#2A2B32] rounded-lg text-white placeholder:text-[#A0AAB2] focus:outline-none focus:border-[#FF6B35]"
                      placeholder="Your name" data-testid="booking-name" />
                  </div>
                  <div>
                    <label className="flex items-center space-x-2 text-sm font-medium text-white mb-2">
                      <Phone size={15} className="text-[#FF6B35]" />
                      <span>Phone *</span>
                    </label>
                    <input type="tel" value={formData.phone} onChange={(e) => setFormData(prev => ({ ...prev, phone: e.target.value }))}
                      className="w-full px-4 py-3 bg-[#1F2027] border border-[#2A2B32] rounded-lg text-white placeholder:text-[#A0AAB2] focus:outline-none focus:border-[#FF6B35]"
                      placeholder="(555) 123-4567" data-testid="booking-phone" />
                  </div>
                  <div>
                    <label className="flex items-center space-x-2 text-sm font-medium text-white mb-2">
                      <FileText size={15} className="text-[#FF6B35]" />
                      <span>Service *</span>
                    </label>
                    <select value={formData.service} onChange={(e) => setFormData(prev => ({ ...prev, service: e.target.value }))}
                      className="w-full px-4 py-3 bg-[#1F2027] border border-[#2A2B32] rounded-lg text-white focus:outline-none focus:border-[#FF6B35]"
                      data-testid="booking-service">
                      <option value="">Select a service</option>
                      {services.map(s => <option key={s} value={s}>{s}</option>)}
                    </select>
                  </div>

                  {/* Optional fields - collapsible */}
                  <details className="group">
                    <summary className="text-[#A0AAB2] text-sm cursor-pointer hover:text-white transition-colors">
                      + Add more details (optional)
                    </summary>
                    <div className="mt-4 space-y-4">
                      <input type="email" value={formData.email} onChange={(e) => setFormData(prev => ({ ...prev, email: e.target.value }))}
                        className="w-full px-4 py-3 bg-[#1F2027] border border-[#2A2B32] rounded-lg text-white placeholder:text-[#A0AAB2] focus:outline-none focus:border-[#FF6B35]"
                        placeholder="Email (optional)" data-testid="booking-email" />
                      <input type="text" value={formData.address} onChange={(e) => setFormData(prev => ({ ...prev, address: e.target.value }))}
                        className="w-full px-4 py-3 bg-[#1F2027] border border-[#2A2B32] rounded-lg text-white placeholder:text-[#A0AAB2] focus:outline-none focus:border-[#FF6B35]"
                        placeholder="Service address (optional)" data-testid="booking-address" />
                      <textarea value={formData.notes} onChange={(e) => setFormData(prev => ({ ...prev, notes: e.target.value }))}
                        rows={3}
                        className="w-full px-4 py-3 bg-[#1F2027] border border-[#2A2B32] rounded-lg text-white placeholder:text-[#A0AAB2] focus:outline-none focus:border-[#FF6B35] resize-none"
                        placeholder="Project details (optional)" data-testid="booking-notes" />
                    </div>
                  </details>

                  <button type="submit" disabled={isSubmitting}
                    className="w-full gradient-btn py-4 rounded-xl font-bold text-lg disabled:opacity-50 shadow-lg shadow-[#FF6B35]/20"
                    data-testid="booking-submit">
                    {isSubmitting ? "Booking..." : "Book My Free Estimate"}
                  </button>
                  <p className="text-center text-[#A0AAB2]/50 text-xs">No spam. We'll only contact you about this project.</p>
                </div>
              </div>

              {/* Right: Calendar (optional) */}
              <div className="bg-[#15161C] border border-[#2A2B32] rounded-xl p-6">
                <h2 className="text-lg font-semibold text-white mb-2 flex items-center space-x-2">
                  <CalendarIcon size={18} className="text-[#FF6B35]" />
                  <span>Preferred Date</span>
                  <span className="text-[#A0AAB2] text-xs font-normal">(optional)</span>
                </h2>
                <p className="text-[#A0AAB2] text-xs mb-4">Pick a preferred date or skip — we'll work with your schedule.</p>
                <div className="flex justify-center">
                  <Calendar mode="single" selected={selectedDate} onSelect={setSelectedDate}
                    disabled={{ before: new Date() }}
                    className="rounded-lg border border-[#2A2B32] bg-[#1F2027] p-3"
                    data-testid="booking-calendar" />
                </div>
                {selectedDate && (
                  <div className="mt-5">
                    <h3 className="text-white font-medium mb-3 flex items-center space-x-2">
                      <Clock size={16} className="text-[#FF6B35]" />
                      <span>Preferred Time</span>
                      <span className="text-[#A0AAB2] text-xs font-normal">(optional)</span>
                    </h3>
                    <div className="grid grid-cols-4 gap-2">
                      {timeSlots.map(time => (
                        <button key={time} type="button"
                          onClick={() => setFormData(prev => ({ ...prev, preferred_time: time }))}
                          className={`py-2 px-2 rounded-lg text-xs font-medium transition-all ${
                            formData.preferred_time === time
                              ? "bg-[#FF6B35] text-white"
                              : "bg-[#1F2027] text-[#A0AAB2] border border-[#2A2B32] hover:border-[#FF6B35]/50"
                          }`}
                          data-testid={`time-slot-${time.replace(/[: ]/g, '-')}`}>
                          {time}
                        </button>
                      ))}
                    </div>
                  </div>
                )}

                {/* Trust signals */}
                <div className="mt-6 pt-5 border-t border-[#2A2B32] space-y-3">
                  {[
                    "100% Free — no obligation",
                    "We call back within minutes",
                    "Licensed & insured in Florida"
                  ].map((text, i) => (
                    <div key={i} className="flex items-center space-x-2">
                      <CheckCircle size={14} className="text-green-400 shrink-0" />
                      <span className="text-[#A0AAB2] text-xs">{text}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </form>
        </div>
      </section>

      {/* Contact bottom */}
      <section className="py-10 bg-[#15161C] border-t border-[#2A2B32]">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <p className="text-[#A0AAB2] mb-3 text-sm">Prefer to call? Reach us directly:</p>
          <div className="flex flex-col sm:flex-row justify-center gap-4">
            <a href="tel:863-340-9244" className="inline-flex items-center justify-center space-x-2 text-[#FF6B35] hover:text-white font-semibold transition-colors">
              <Phone size={18} /><span>863-340-9244</span>
            </a>
            <span className="hidden sm:inline text-[#2A2B32]">|</span>
            <a href="tel:863-340-8262" className="inline-flex items-center justify-center space-x-2 text-[#FF6B35] hover:text-white font-semibold transition-colors">
              <Phone size={18} /><span>863-340-8262</span>
            </a>
          </div>
        </div>
      </section>
    </div>
  );
};

export default BookingPage;
